import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Moment from 'moment';
import { toast } from 'react-toastify';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
} from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Backdrop from '@mui/material/Backdrop';
import LinearProgress from '@mui/material/LinearProgress';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import Auth from '../../../data/auth-service';
import SuricataService from '../../../data/suricata-service';
import SystemService from '../../../data/system-service';

import Validator, { useValidate, checkValidity } from '../../../components/validator';
import ConfirmationDialog from '../../../components/confirmationdlg';

const useStyles = makeStyles(theme => ({
    justifySpace: {
        justifyContent: 'space-between',
    },
    formControl: {
        margin: theme.spacing(1),
        width: '100%',
    },
    ruleGroups: {
        maxHeight: '150px',
    },
    rulesCard: {
        height: '50%',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(1),
    },
    controlBar: {
        display: 'none',
    },
    hideFileInput: {
        display: 'none',
    },
    buttonMargin: {
        marginLeft: theme.spacing(1),
    },
    highlightText: {
        color: theme.palette.primary.main,
    },
    highlightSuccess: {
        color: theme.palette.success.main,
    },
    highlightError: {
        color: theme.palette.error.main,
    },
    backdrop: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        zIndex: 10000,
    },
    banner: {
        background: theme.styled.paper.background,
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.info.main}`,
        marginBottom: theme.spacing(2),
    }
}));

const SuricataSettings = () => {
    const classes = useStyles();
    const theme = useTheme();

    const navigate = useNavigate();
    const [gcOpen, setGCOpen] = useState(false);
    const [gcPreview, setGCPreview] = useState({ count: 0, threshold: '' });
    const [ruleFile, setRuleFile] = useState({ name: '', content: null });
    const [urls, setURLs] = useState({ url_list: [''] });
    const [systemSettings, setSystemSettings] = useState({
        garbage_collection: {
            auto_run: false,
            inactivity_threshold: 90,
        }
    });
    const [gcResult, setGCResult] = useState({
        report: '',
        rules: 0,
        rules_jobs: 0,
        rules_versions: 0,
        threshold: '',
    });
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [ogURL, setOGURL] = useState(null);
    const [ogGC, setOGGC] = useState(null);
    const [changesURL, setChangesURL] = useState(false);
    const [changesGC, setChangesGC] = useState(false);
    const urlForm = useValidate({});
    const gcForm = useValidate({});
    const [disableFileImport, setDisableFileImport] = useState(false);
    const [disableURLImport, setDisableURLImport] = useState([]);
    const [isImporting, setIsImporting] = useState(false);
    const [importingStatus, setImportingStatus] = useState('');
    const suriPerms = Auth.permissions.get('SuricataRules');
    const [managed, setManaged] = useState(false);

    const MC2Check = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

    useEffect(() => {
        getURLSync();
        getGC();
        getIsManaged();
    }, []);

    useEffect(() => {
        setChangesURL(!_isEqual(urls, ogURL));
    }, [urls]);

    useEffect(() => {
        setChangesGC(!_isEqual(systemSettings, ogGC));
    }, [systemSettings]);

    const getURLSync = () => {
        SuricataService.listSuricataSyncURLs().then(res => {
            if (!res.url_list.length) {
                res.url_list = [''];
            }
            setOGURL(_cloneDeep(res));
            setURLs(res);
        });
    };

    const getGC = () => {
        SystemService.getSystemSettings().then(res => {
            setOGGC(_cloneDeep(res));
            setSystemSettings(res);
        });
    };

    const getIsManaged = () => {
        SuricataService.isManaged().then(res => {
            setManaged((res || {}).managed);
        });
    };

    const handleImport = (i) => {
        const d = [...disableURLImport];
        d[i] = true;
        setDisableURLImport(d);
        setIsImporting(true);
        SuricataService.importFromURL({ url: urls.url_list[i] }).then(res => {
            console.log(res);
            // setImportingStatus(res);
            // toast.success('Rules imported from URL successfully');
            toast.success(res);
            const u = [...disableURLImport];
            u[i] = false;
            setDisableURLImport(u);
        }, err => {
            toast.error(`${err.message}: ${err.error}`);
            const u = [...disableURLImport];
            u[i] = false;
            setDisableURLImport(u);
        }).finally(() => {
            setIsImporting(false);
        });
    };

    const handleUpdateUrl = (e, i, validID, valid) => {
        urlForm.set({ ...urlForm.get(), [validID]: valid });

        const s = { ...urls };
        s.url_list[i] = e.target.value;
        setURLs(s);
    };

    const handleAddURL = (e) => {
        const s = { ...urls };
        const d = [...disableURLImport];
        s.url_list.push('');
        d.push(false);
        setURLs(s);
        setDisableURLImport(d);
    };

    const handleRemoveURL = (e, i) => {
        const s = { ...urls };
        const d = [...disableURLImport];
        s.url_list.splice(i, 1);
        d.splice(i, 1);

        if (!s.url_list.length) {
            s.url_list.push('');
        }

        setURLs(s);
        setDisableURLImport(d);
    };

    const handleSaveImports = () => {
        SuricataService.importSuricataRules(urls).then(res => {
            toast.success('URL list saved successfully');
            getURLSync();
        });
    };

    const handleRevertChanges = () => {
        getURLSync();
        getGC();
        setRuleFile({
            name: '',
            content: null,
        });
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    const handleGetFile = (e) => {
        if (e.target.files[0]) {
            setRuleFile(e.target.files[0]);
        } else {
            setRuleFile({ name: '', content: null });
        }
    };

    const handleUploadFile = () => {
        setDisableFileImport(true);
        setIsImporting(true);
        SuricataService.importFromFile(ruleFile).then(res => {
            if (res.status === 503) {
                setIsImporting(false);
                toast.error('Invalid file upload');
            } else if (res.status === 400) {
                setIsImporting(false);
                toast.error('No file to upload');
            } else if ([200, 204].includes(res.status)) {
                res.text().then((text) => {
                    setImportingStatus(text);
                });
                toast.success('Suricata rule(s) imported successfully');
            }
            setDisableFileImport(false);
        });
    };

    const handleUpdateGCAutoRun = () => {
        const s = { ...systemSettings };
        s.garbage_collection.auto_run = !s.garbage_collection.auto_run;
        setSystemSettings(s);
    };

    const handleUpdateGCThreshold = (e, validID, valid) => {
        gcForm.set({ ...gcForm.get(), [validID]: valid });

        const s = { ...systemSettings };
        s.garbage_collection.inactivity_threshold = parseInt(e.target.value) || 0;
        setSystemSettings(s);
    };

    const handlePreviewGC = () => {
        SuricataService.fastPreviewGarbageCollection().then(res => {
            setGCPreview(res);
            setGCOpen(true);
        });
    };

    const handleRunGC = () => {
        setGCOpen(false);
        toast.info('Beginning Garbage Collection...');
        SuricataService.runGarbageCollection().then(res => {
            setGCResult(res);
            setConfirmOpen(true);
        });
    };

    const handleCloseGC = () => {
        setGCOpen(false);
    };

    const handleSaveGC = () => {
        SystemService.updateSystemSettings(systemSettings).then(res => {
            toast.success('Garbage collection settings updated successfully');
        }, err => {
            toast.error(`${err.message}: ${err.error}`);
        });
    };

    const handleCloseConfirm = () => {
        setConfirmOpen(false);
    };

    return (
        <main>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Button className={classes.buttonRight} variant="contained" color="primary" aria-label="revert" onClick={handleRevertChanges}>Revert</Button>
                    <Button className={classes.buttonRight} variant="contained" aria-label="back" onClick={handleGoBack}>Back</Button>
                </Grid>
                <Grid container item xs={8} spacing={2}>
                    <Grid item xs={12}>
                        <Paper elevation={3}>
                            <Box p={3}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="h5">Import Rules</Typography>
                                        {!MC2Check && managed && <Box mt={2} className={classes.banner}>
                                            This CMC is Managed by MC2. Rule imports must be set on MC2.
                                        </Box>}
                                        <Box mt={2}>
                                            <Typography>Note: Clicking Import will trigger immediate rule import from File/URL.</Typography>
                                            <Typography>Clicking Save All URL will store URLs for periodic imports.</Typography>
                                            <Typography>For File/URL Import, only the following extensions are supported: .rules, .tar, .zip, .tgz and .tar.gz.</Typography>
                                            <Typography>Please note that only files with {'".rules"'} extension are imported from .tar, .zip, .tgz and .tar.gz.</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper elevation={3}>
                            <Box p={3}>
                                <Grid container spacing={2}>
                                    <Grid item container xs={12}>
                                        <Grid item xs={12}><Typography variant="h6">Import From File</Typography></Grid>
                                    </Grid>
                                    <Grid item container xs={12}>
                                        <Grid item xs={8}><Typography>File Name</Typography></Grid>
                                        <Grid item xs={4}><Typography>Actions</Typography></Grid>
                                        <Grid item xs={8}>{ruleFile && ruleFile.name}</Grid>
                                        <Grid item xs={4} container justifyContent="flex-end">
                                            <input
                                                disabled={managed || !suriPerms.write}
                                                id="suricata-rules-file"
                                                className={classes.hideFileInput}
                                                type="file"
                                                onChange={handleGetFile}
                                            />
                                            <label htmlFor="suricata-rules-file">
                                                <Button
                                                    disabled={managed || !suriPerms.write}
                                                    aria-label="browse suricata rules file"
                                                    variant="contained"
                                                    color="primary"
                                                    component="span"
                                                >Browse</Button>
                                            </label>
                                            <Button
                                                className={classes.buttonMargin}
                                                variant="contained"
                                                color="primary"
                                                aria-label="import suricata rules file"
                                                onClick={handleUploadFile}
                                                disabled={(managed || !suriPerms.write || (ruleFile && !ruleFile.name)) || disableFileImport}
                                            >Import</Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper elevation={3}>
                            <Box p={3}>
                                <Grid container spacing={2}>
                                    <Grid item container xs={12}>
                                        <Grid item xs={12}><Typography variant="h6">Import From URL(s)</Typography></Grid>
                                    </Grid>
                                    <Grid item container xs={12}>
                                        {urls.url_list.map((v, i) => {
                                            return (
                                                <React.Fragment key={'url-list-' + i}>
                                                    <Grid item xs={12}>
                                                        <Box mb={2}>
                                                            <Validator
                                                                allowEmpty
                                                                valueType="url"
                                                                errorText="File URL is invalid"
                                                                fullWidth
                                                                value={v}
                                                                onChange={(e, validID, valid) => { handleUpdateUrl(e, i, validID, valid) }}
                                                                endAdornment={
                                                                    <InputAdornment position='end'>
                                                                        <IconButton
                                                                            aria-label="delete url"
                                                                            disabled={managed || !suriPerms.delete}
                                                                            onClick={(e) => { handleRemoveURL(e, i) }}
                                                                            size="large">
                                                                            <DeleteIcon />
                                                                        </IconButton>
                                                                        <Button
                                                                            variant="contained"
                                                                            color="primary"
                                                                            aria-label="import suricata rules url"
                                                                            onClick={() => { handleImport(i) }}
                                                                            disabled={managed || !suriPerms.write || disableURLImport[i]}
                                                                            style={{ paddingTop: '5px', paddingBottom: '5px', marginBottom: '3px' }}
                                                                        >Import</Button>
                                                                    </InputAdornment>
                                                                }
                                                            />
                                                        </Box>
                                                    </Grid>
                                                </React.Fragment>
                                            );
                                        })}
                                    </Grid>
                                    <Grid item xs={12} container justifyContent="flex-end">
                                        <Button
                                            aria-label="add url"
                                            disabled={managed || !suriPerms.write}
                                            variant="contained"
                                            onClick={handleAddURL}>
                                            <AddIcon/> Add URL
                                        </Button>
                                        <Button
                                            className={classes.buttonMargin}
                                            variant="contained"
                                            onClick={handleSaveImports}
                                            aria-label="save urls"
                                            disabled={managed || !suriPerms.write || !(checkValidity(urlForm.get()) && changesURL)}
                                        >Save URLs</Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
                {!MC2Check && <Grid item xs={4}>
                    <Paper elevation={3}>
                        <Box p={3}>
                            <Typography variant="h5">Garbage Collection</Typography>
                            <Box mt={2}>
                                <FormControlLabel
                                    label="Auto Run"
                                    control={
                                        <Checkbox
                                            disabled={managed || !suriPerms.override}
                                            checked={systemSettings.garbage_collection.auto_run}
                                            onChange={handleUpdateGCAutoRun}
                                        />
                                    }
                                />
                            </Box>
                            <Box mt={2}>
                                <Validator
                                    disabled={managed || !suriPerms.override}
                                    valueType="range"
                                    errorText="Threshold must be between 7 and 365 days"
                                    inputProps={{
                                        min: 7,
                                        max: 365,
                                    }}
                                    fullWidth
                                    label="Inactivity Threshold"
                                    type="number"
                                    value={systemSettings.garbage_collection.inactivity_threshold}
                                    onChange={handleUpdateGCThreshold}
                                />
                            </Box>
                            <Box mt={2} display="flex" justifyContent="flex-end">
                                <Button
                                    aria-label="run preview"
                                    disabled={managed || !suriPerms.override}
                                    variant="contained"
                                    color="primary"
                                    onClick={handlePreviewGC}>Run Preview</Button>
                                <Button
                                    className={classes.buttonMargin}
                                    variant="contained"
                                    color="primary"
                                    aria-label="save garbage collection"
                                    onClick={handleSaveGC}
                                    disabled={managed || !suriPerms.override || !(checkValidity(gcForm.get()) && changesGC)}
                                >Save</Button>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>}
            </Grid>
            <Backdrop className={classes.backdrop} open={isImporting}>
                <Paper elevation={3}>
                    <Box display={importingStatus ? 'none' : 'block'}><LinearProgress /></Box>
                    <Box p={4}>
                        <Box display={importingStatus ? 'none' : 'block'}><Typography>Starting import...</Typography></Box>
                        {importingStatus && importingStatus.replaceAll('"', '').split(/\\n|\n|\\r|\r/).filter(v => Boolean(v)).map((v, i) => {
                            const t = v.split(':');
                            if (t[0].includes('Ignored') && parseInt(t[1]) !== 0) {
                                return (
                                    <Box key={'import-status-' + i}><Typography>{t[0]}: <span className={classes.highlightError}>{t[1]}</span></Typography></Box>
                                );
                            }
                            return (
                                <Box key={'import-status-' + i}><Typography>{t[0]}: <span className={classes.highlightSuccess}>{t[1]}</span></Typography></Box>
                            );
                        })}
                        <Box mt={2} display={importingStatus ? 'block' : 'none'}></Box>
                        <Box mt={2} display={importingStatus ? 'flex' : 'none'} justifyContent="flex-end">
                            <Button
                                disabled={managed}
                                variant="contained"
                                color="primary"
                                aria-label="ok"
                                onClick={() => {
                                    setIsImporting(false);
                                    setImportingStatus('');
                                }}
                            >Ok</Button>
                        </Box>
                    </Box>
                </Paper>
            </Backdrop>
            <ConfirmationDialog
                open={confirmOpen}
                onClose={handleCloseConfirm}
                onOK={handleCloseConfirm}
                nocancel
                title="Garbage Collection Successful"
                okLabel="Ok"
                message={(() => {
                    return (
                        <Box>
                            <Box mt={3}><Typography variant="body2">Detailed purge report is location at <span className={classes.highlightText}>{(gcResult && gcResult.report) || ''}</span></Typography></Box>
                        </Box>
                    );
                })()}
            />
            <ConfirmationDialog
                onClose={handleCloseGC}
                onOK={handleRunGC}
                nook={!gcPreview.count}
                open={gcOpen}
                message={(() => {
                    let gcMsg;
                    if (gcPreview.count) {
                        gcMsg = (
                            <React.Fragment>
                                <Box mt={1}>
                                    <Typography>
                                        There are <span className={classes.highlightText}>{gcPreview.count}</span> rules that have not been
                                        seen since <span className={classes.highlightText}>{Moment(gcPreview.threshold).format(theme.date.short)}</span>
                                    </Typography>
                                </Box>
                                <Box mt={1}><Typography>Detailed Garbage Collection reports are kept on the CMC for 42 days at <span className={classes.highlightText}>/var/bricata/gc_reports</span></Typography></Box>
                                <Box mt={1}><Typography>Rules can be removed from the purge candidate list in the rules list below.</Typography></Box>
                            </React.Fragment>
                        );
                    } else {
                        gcMsg = (
                            <Box mt={1}>
                                <Typography>
                                    There are no rules eligible for garbage collection.
                                </Typography>
                            </Box>
                        );
                    }

                    return (
                        <Box>{gcMsg}</Box>
                    );
                })()}
                title="Run Garbage Collection"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
        </main>
    );
};

export default SuricataSettings;
